import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, TriangleAlert, Shield } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const EAuctionLegalInformationPage = () => {
    const [loading, setLoading] = useState(true);

    // Legal Settings State
    const [legalSettings, setLegalSettings] = useState({
        section_title: '',
        section_description: '',
        regulatory_title: '',
        regulatory_description: '',
        button_text: '',
        button_link: '',
        is_active: true
    });

    // Important Points State
    const [importantPoints, setImportantPoints] = useState([]);
    const [isPointModalOpen, setIsPointModalOpen] = useState(false);
    const [pointModalData, setPointModalData] = useState({
        point_text: '',
        display_order: 0,
        is_active: true
    });
    const [editingPoint, setEditingPoint] = useState(null);
    const [pointDeleteConfirm, setPointDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsResponse, pointsResponse] = await Promise.all([
                axios.get('/api/admin/e-auction-properties/settings/legal'),
                axios.get('/api/admin/e-auction-properties/important-points')
            ]);

            if (settingsResponse.data.success && settingsResponse.data.data) {
                setLegalSettings(settingsResponse.data.data);
            }
            if (pointsResponse.data.success) {
                setImportantPoints(pointsResponse.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleLegalSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setLegalSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLegalSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/e-auction-properties/settings/legal', legalSettings);
            toast.success('Legal settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleAddPoint = () => {
        setEditingPoint(null);
        setPointModalData({
            point_text: '',
            display_order: importantPoints.length + 1,
            is_active: true
        });
        setIsPointModalOpen(true);
    };

    const handleEditPoint = (point) => {
        setEditingPoint(point);
        setPointModalData({
            ...point,
            is_active: Boolean(point.is_active)
        });
        setIsPointModalOpen(true);
    };

    const handleStatusToggle = async (point) => {
        try {
            const updatedPoint = { ...point, is_active: !point.is_active };
            await axios.put(`/api/admin/e-auction-properties/important-points/${point.id}`, updatedPoint);
            toast.success('Point status updated');
            fetchData();
        } catch (error) {
            console.error('Error updating point status:', error);
            toast.error('Failed to update point status');
        }
    };

    const handlePointSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPoint) {
                await axios.put(`/api/admin/e-auction-properties/important-points/${editingPoint.id}`, pointModalData);
                toast.success('Important point updated');
            } else {
                await axios.post('/api/admin/e-auction-properties/important-points', pointModalData);
                toast.success('Important point created');
            }
            setIsPointModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving point:', error);
            toast.error('Failed to save point');
        }
    };

    const confirmDeletePoint = async () => {
        try {
            await axios.delete(`/api/admin/e-auction-properties/important-points/${pointDeleteConfirm.id}`);
            toast.success('Important point deleted');
            setPointDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting point:', error);
            toast.error('Failed to delete point');
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Legal Information Section</h1>
                    <p className="text-slate-500 mt-1">Manage the legal information and compliance section</p>
                </div>
            </div>

            {/* Section Header Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-slate-800">Section Header Settings</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={legalSettings.section_title || ''}
                            onChange={handleLegalSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="Compliance & Legal Information"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                        <textarea
                            name="section_description"
                            value={legalSettings.section_description || ''}
                            onChange={handleLegalSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                            placeholder="The e-auction is conducted in accordance with applicable laws..."
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleLegalSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Regulatory Advisory Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-slate-800">Regulatory Advisory Settings</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Regulatory Title</label>
                        <input
                            type="text"
                            name="regulatory_title"
                            value={legalSettings.regulatory_title || ''}
                            onChange={handleLegalSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="Regulatory Advisory"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Regulatory Description</label>
                        <textarea
                            name="regulatory_description"
                            value={legalSettings.regulatory_description || ''}
                            onChange={handleLegalSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                            placeholder="Disputes, if any, will be handled as per applicable legal provisions."
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Button Text</label>
                            <input
                                type="text"
                                name="button_text"
                                value={legalSettings.button_text || ''}
                                onChange={handleLegalSettingsChange}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                placeholder="Contact Team"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-2">Button Link</label>
                            <input
                                type="text"
                                name="button_link"
                                value={legalSettings.button_link || ''}
                                onChange={handleLegalSettingsChange}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                                placeholder="/contact"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleLegalSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Important Points Management */}
            <div>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Important Points</h2>
                        <p className="text-slate-500 text-sm mt-1">Manage the important notices displayed to users</p>
                    </div>
                    <button
                        onClick={handleAddPoint}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Point
                    </button>
                </div>

                {importantPoints.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-slate-200">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <TriangleAlert className="text-slate-300" size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">No important points added yet</h3>
                        <p className="text-slate-500 mb-6 max-w-sm mx-auto">Add important notices that users should be aware of before participating.</p>
                        <button
                            onClick={handleAddPoint}
                            className="text-primary-600 font-semibold hover:text-primary-700"
                        >
                            Add your first point
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {importantPoints.map((point) => (
                            <div key={point.id} className="bg-white rounded-xl p-6 shadow-sm border border-slate-100 hover:shadow-md transition-all">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-4 flex-1">
                                        <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <TriangleAlert className="text-amber-600" size={20} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-slate-700 leading-relaxed">{point.point_text}</p>
                                            <div className="flex items-center gap-3 mt-3">
                                                <span className="text-xs text-slate-500">Order: {point.display_order}</span>
                                                <span className={`text-xs px-2 py-1 rounded-full ${point.is_active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                                    {point.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={Boolean(point.is_active)}
                                                onChange={() => handleStatusToggle(point)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        </label>
                                        <button
                                            onClick={() => handleEditPoint(point)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                        >
                                            <Pencil size={18} />
                                        </button>
                                        <button
                                            onClick={() => setPointDeleteConfirm({ isOpen: true, id: point.id })}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Point Modal */}
            {isPointModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingPoint ? 'Edit Important Point' : 'Add New Important Point'}</h2>
                            <button onClick={() => setIsPointModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handlePointSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Point Text</label>
                                <textarea
                                    value={pointModalData.point_text}
                                    onChange={(e) => setPointModalData({ ...pointModalData, point_text: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    placeholder="Enter important point text..."
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Display Order</label>
                                    <input
                                        type="number"
                                        value={pointModalData.display_order}
                                        onChange={(e) => setPointModalData({ ...pointModalData, display_order: parseInt(e.target.value) })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        min="1"
                                        required
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={pointModalData.is_active}
                                            onChange={(e) => setPointModalData({ ...pointModalData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{pointModalData.is_active ? 'Active' : 'Inactive'}</span>
                                    </label>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsPointModalOpen(false)}
                                    className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg"
                                >
                                    {editingPoint ? 'Update Point' : 'Create Point'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={pointDeleteConfirm.isOpen}
                onClose={() => setPointDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeletePoint}
                title="Delete Important Point"
                message="Are you sure you want to delete this important point? This action cannot be undone."
            />
        </div>
    );
};

export default EAuctionLegalInformationPage;
